import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { GoogleLogo, Check, Warning, ArrowCounterClockwise, SignOut, SpinnerGap } from "@phosphor-icons/react";

export function IntegrationsTab() {
  const { api } = useAuth();
  const [gdocsStatus, setGdocsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const resp = await api.get("/google/status");
      setGdocsStatus(resp.data);
    } catch {
      setGdocsStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  // Listen for postMessage from OAuth popup
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "google-auth-success") {
        toast.success("Google Docs connected!");
        checkStatus();
      } else if (event.data?.type === "google-auth-error") {
        toast.error("Google connection failed. Please try again.");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [checkStatus]);

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      const resp = await api.post("/google/init-auth");
      const url = resp.data.authorization_url;
      window.open(url, "google-auth", "width=520,height=640,left=200,top=100");
    } catch {
      toast.error("Failed to start Google connection.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    try {
      await api.post("/google/disconnect");
      setGdocsStatus({ connected: false });
      toast.success("Google Docs disconnected. Previously exported docs are still available in your Google Drive.");
    } catch {
      toast.error("Failed to disconnect.");
    } finally {
      setActionLoading(false);
    }
  };

  const connected = gdocsStatus?.connected;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="integrations-tab">
      <div>
        <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)] mb-1">Integrations</h2>
        <p className="text-sm text-[var(--text-muted)]">Manage external service connections for exporting and sharing content.</p>
      </div>

      {/* Google Docs Card */}
      <section
        className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5"
        data-testid="gdocs-integration-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
              <GoogleLogo size={24} weight="bold" className="text-[var(--text-primary)]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">Google Docs</h3>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">Export generated content directly to Google Docs in your Drive.</p>
            </div>
          </div>

          {loading ? (
            <SpinnerGap size={20} className="animate-spin text-[var(--text-muted)]" />
          ) : (
            <div className="flex items-center gap-2">
              {connected ? (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20" data-testid="gdocs-connected-badge">
                  <Check size={12} weight="bold" className="text-emerald-400" />
                  <span className="text-[11px] font-semibold text-emerald-400">Connected</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--bg-base)] border border-[var(--border)]" data-testid="gdocs-disconnected-badge">
                  <Warning size={12} className="text-[var(--text-muted)]" />
                  <span className="text-[11px] font-medium text-[var(--text-muted)]">Not connected</span>
                </span>
              )}
            </div>
          )}
        </div>

        {!loading && (
          <div className="mt-4 pt-4 border-t border-[var(--border)]">
            {connected ? (
              <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--text-muted)]">
                  Your Google account is connected. Content exports will create docs in your Google Drive.
                  Disconnecting will not remove previously exported documents.
                </p>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <button
                    onClick={handleConnect}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-colors disabled:opacity-50"
                    data-testid="gdocs-reconnect-btn"
                  >
                    {actionLoading ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowCounterClockwise size={12} />}
                    Reconnect
                  </button>
                  <button
                    onClick={handleDisconnect}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    data-testid="gdocs-disconnect-btn"
                  >
                    {actionLoading ? <SpinnerGap size={12} className="animate-spin" /> : <SignOut size={12} />}
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--text-muted)]">
                  Connect your Google account to enable one-click export of drafts to Google Docs.
                </p>
                <button
                  onClick={handleConnect}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#FF4400] hover:bg-[#FF6633] text-white transition-colors disabled:opacity-50 shrink-0 ml-4"
                  data-testid="gdocs-connect-btn"
                >
                  {actionLoading ? <SpinnerGap size={14} className="animate-spin" /> : <GoogleLogo size={14} weight="bold" />}
                  Connect Google Docs
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Placeholder for future integrations */}
      <section className="border border-dashed border-[var(--border)] rounded-xl p-5 text-center">
        <p className="text-sm text-[var(--text-muted)]">More integrations coming soon — Slack, Notion, and more.</p>
      </section>
    </motion.div>
  );
}
